import { useCallback, useMemo, useRef, useState } from 'react';
import compact from 'lodash/compact';
import useOutsideClick from 'src/hooks/useOutsideClick';
import { SKU_CATEGORY_IDS, SUB_CATEGORIES } from '../constants';

const COLUMNS_TO_DISPLAY = [
  [SKU_CATEGORY_IDS.POPULAR_SERVICES],
  [SKU_CATEGORY_IDS.SMART_HOME, SKU_CATEGORY_IDS.AUDIO_VIDEO, SKU_CATEGORY_IDS.MOBILE_DEVICES],
  [
    SKU_CATEGORY_IDS.WIFI_NETWORK,
    SKU_CATEGORY_IDS.HOME_SECURITY,
    SKU_CATEGORY_IDS.COMPUTERS_PRINTERS,
    SKU_CATEGORY_IDS.HOUSE_CLEANING,
  ],
  [SKU_CATEGORY_IDS.TV_MOUNTING, SKU_CATEGORY_IDS.AROUND_THE_HOME],
];

const createGroupedColumns = (skuCategoriesAndSkus) => {
  const cols = COLUMNS_TO_DISPLAY.reduce((columnGroup, column) => {
    columnGroup.push(
      compact(
        column.map((c) => {
          return skuCategoriesAndSkus.find((sku) => sku.id === c.id);
        }),
      ),
    );
    return columnGroup;
  }, []);

  // Overwrite the first column with "popularServicesCategory" based on SUB_CATEGORIES
  const popularServicesCategory = [SUB_CATEGORIES[SUB_CATEGORIES.length - 1]];
  cols[0] = popularServicesCategory;

  return cols;
};

export default function useTopNavItems({ skuCategoriesAndSkus = [], toggleTopnav = () => {} }) {
  // ######
  // STATE
  // ######
  const [showSecondaryNav, setShowSecondaryNav] = useState(false);
  const [shouldSlide, setShouldSlide] = useState(false);
  const [currentSelection, setCurrentSelection] = useState([
    { label: '', skus: [], subCategorySkus: [] },
  ]);
  const [selectedSubCategory, setSelectedSubCategory] = useState({});

  // #####
  // REFS
  // #####
  const wrapperRef = useRef(null);

  // ########
  // HANDLERS
  // ########
  const handleSubCategoryClick = useCallback(
    (event, subCategory, category) => {
      event.stopPropagation();
      if (subCategory.showSecondaryNav) {
        // Setting up for nested (secondary) nav
        setCurrentSelection([
          { label: category.name },
          {
            label: subCategory.name,
            skus: category.skus,
            subCategorySkus: subCategory.skus,
          },
        ]);
        setShouldSlide(false);
        setShowSecondaryNav(true);
        setSelectedSubCategory(subCategory);
      } else {
        // If there's no secondary nav to show, close top nav
        toggleTopnav();
      }
    },
    [toggleTopnav],
  );

  const handleBackClick = useCallback((event) => {
    event.stopPropagation();
    setShouldSlide(true);
    setShowSecondaryNav(false);
  }, []);

  /** Used when a user clicks on a primary or secondary menu link that leads to a new location */
  const handleLocationChange = () => {
    toggleTopnav();
  };

  // #############
  // DERIVED DATA
  // #############
  const groupedColumns = useMemo(() => createGroupedColumns(skuCategoriesAndSkus), [
    skuCategoriesAndSkus,
  ]);

  // Close on outside click
  useOutsideClick(wrapperRef, toggleTopnav);

  return {
    // State
    showSecondaryNav,
    shouldSlide,
    currentSelection,
    selectedSubCategory,

    // Setters
    setShowSecondaryNav,
    setShouldSlide,
    setCurrentSelection,
    setSelectedSubCategory,

    // Refs
    wrapperRef,

    // Handlers
    handleSubCategoryClick,
    handleBackClick,
    handleLocationChange,

    // Derived Data
    groupedColumns,
  };
}
